import {
  Form,
  SingleSelectDropdown,
  UploadInput,
  UploadImagePreview,
  FormTextInput,
  FormLinksInput,
} from 'ck-components';
import { SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import { EditOrganizerV1 } from 'ck-queries';
import { convertFileToUrlString } from 'ck-utils';
import toast from 'react-hot-toast';
import { useOrganizer } from '../../queries/organizer/useOrganizers';
import { useEditOrganizer } from '../../queries/organizer/useEditOrganizer';
import { useOrganizerCommittees } from '../../queries/organizer/useCommittees';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

interface Props {
  onOrganizerChange?: (
    organizer: { id: string; name: string } | undefined
  ) => void;
  /**
   * Runs before the organizer page itself is saved, so the categories PUT (cheap, no
   * blob upload) lands first and a failure there aborts the page save rather than
   * re-uploading images for a save that's about to fail anyway. Resolving false aborts.
   */
  beforeSubmit?: () => Promise<boolean>;
}

export interface OrganizerFormHandle {
  /** Validates and saves the organizer page fields. Runs beforeSubmit (the categories
   *  save) first internally, and aborts without saving anything if that fails. */
  submit: () => Promise<void>;
}

export const EditOrganizerForm = forwardRef<OrganizerFormHandle, Props>(
  ({ onOrganizerChange, beforeSubmit }, ref) => {
    const Q_committees = useOrganizerCommittees();
    const form = useForm<EditOrganizerV1>({
      defaultValues: { links: [], description: '' },
    });

    const selectedOrganizer = useMemo(() => {
      if (Q_committees.data) {
        return Q_committees.data.find(
          (committee) => committee.name === form.watch('name')
        );
      }
    }, [form.watch('name'), Q_committees]);

    useEffect(() => {
      onOrganizerChange?.(selectedOrganizer);
    }, [selectedOrganizer]);

    const { data: Q_organizer } = useOrganizer(selectedOrganizer?.id);
    const M_editOrganizer = useEditOrganizer(selectedOrganizer?.id ?? '');

    const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | undefined>();
    const [coverPreviewUrl, setCoverPreviewUrl] = useState<
      string | undefined
    >();

    const logo = form.watch('logo');
    const backgroundImage = form.watch('backgroundImage');

    // Only prefill once per selected organizer, so a background refetch (e.g. on
    // window focus) doesn't clobber unsaved edits with the last-saved server state.
    const prefilledOrganizerId = useRef<string | undefined>();

    const flushPendingLink = useRef<(() => boolean) | null>(null);

    useEffect(() => {
      if (
        Q_organizer &&
        prefilledOrganizerId.current !== selectedOrganizer?.id
      ) {
        prefilledOrganizerId.current = selectedOrganizer?.id;
        form.setValue('description', '');
        form.setValue('phoneNumber', Q_organizer.phoneNumber ?? '');
        form.setValue('email', Q_organizer.email ?? '');
        form.setValue('links', Q_organizer.links ?? []);
        setLogoPreviewUrl(Q_organizer.logoUrl ?? undefined);
        setCoverPreviewUrl(Q_organizer.backgroundImageUrl ?? undefined);
      }
    }, [Q_organizer, selectedOrganizer?.id]);

    useEffect(() => {
      if (logo instanceof File) {
        convertFileToUrlString(logo).then(
          (url) => url && setLogoPreviewUrl(url)
        );
      }
    }, [logo]);

    useEffect(() => {
      if (backgroundImage instanceof File) {
        convertFileToUrlString(backgroundImage).then(
          (url) => url && setCoverPreviewUrl(url)
        );
      }
    }, [backgroundImage]);

    const onInvalid: SubmitErrorHandler<EditOrganizerV1> = () => {
      toast.error('Fyll i alla obligatoriska fält');
    };

    const onSubmit: SubmitHandler<EditOrganizerV1> = async (data) => {
      if (!selectedOrganizer?.id) return;
      try {
        const categoriesOk = (await beforeSubmit?.()) ?? true;
        if (!categoriesOk) return;

        await M_editOrganizer.mutateAsync(data);
        // Allow the next Q_organizer refetch (triggered by this mutation's own
        // invalidation) to re-prefill the form with the freshly saved server state.
        prefilledOrganizerId.current = undefined;
      } catch {
        // The mutation's own toast.promise already surfaced the error.
      }
    };

    const triggerSubmit = () => {
      // A link typed into the inputs but never confirmed with Enter or the checkmark is not
      // in form state yet. Commit it here so saving cannot quietly drop it; a false return
      // means it is incomplete and the field is already showing why.
      if (flushPendingLink.current && !flushPendingLink.current()) return;
      return form.handleSubmit(onSubmit, onInvalid)();
    };

    useImperativeHandle(ref, () => ({
      submit: async () => {
        await triggerSubmit();
      },
    }));

    return (
      <Form
        buttonType='button'
        submitButtonStyle='w-52'
        form={form}
        hideSubmitButton
        onSubmit={triggerSubmit}
        submitButtonLabel='Spara uppdateringar'
        ignoreGap
      >
        {Q_committees.data && (
          <SingleSelectDropdown
            withSearch
            label='Namn'
            name='name'
            form={form}
            values={Q_committees.data.map((committee) => committee.name)}
            labelFromValue={(value) => value}
            description={'Namnet hämtas från organisationer'}
          />
        )}
        <UploadInput
          parentForm={form}
          label='Logo'
          name='logo'
          accept='.jpeg, .jpg, .png'
          emptyState='Ladda upp bild / Upload image'
          description={''}
        />
        {logoPreviewUrl && (
          <div className='mb-5'>
            <UploadImagePreview imageUrl={logoPreviewUrl} />
          </div>
        )}
        <UploadInput
          parentForm={form}
          label='Omslagsbild'
          name='backgroundImage'
          accept='.jpeg, .jpg, .png'
          emptyState='Ladda upp bild / Upload image'
          description={''}
        />
        {coverPreviewUrl && (
          <div className='mb-5'>
            <UploadImagePreview imageUrl={coverPreviewUrl} />
          </div>
        )}
        <FormTextInput
          label='Telefonnummer'
          name='phoneNumber'
          type='tel'
          parentForm={form}
          optional
          description=''
          onKeyDown={(e) => {
            const allowedKeys = [
              'Backspace',
              'Delete',
              'Tab',
              'ArrowLeft',
              'ArrowRight',
              'ArrowUp',
              'ArrowDown',
              'Home',
              'End',
            ];
            if (e.ctrlKey || e.metaKey || allowedKeys.includes(e.key)) return;
            if (!/^[0-9+\s-]$/.test(e.key)) e.preventDefault();
          }}
          options={{
            validate: (value: string) => {
              if (!value) return true;
              const digits = value.replace(/[\s-]/g, '');
              return (
                /^(\+46|0)[1-9]\d{6,8}$/.test(digits) ||
                'Ogiltigt telefonnummer'
              );
            },
          }}
        />
        <FormTextInput
          label='Email'
          name='email'
          type='email'
          parentForm={form}
          optional
          description=''
          options={{
            pattern: {
              value:
                // eslint-disable-next-line no-useless-escape
                /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
              message: 'Ogiltig e-postadress',
            },
          }}
        />
        <FormLinksInput
          label='Länkar'
          name='links'
          parentForm={form}
          maxLinks={3}
          flushRef={flushPendingLink}
        />
        {/*<div>*/}
        {/*  <FormMultiLineTextInput*/}
        {/*    label='Beskrivning'*/}
        {/*    name='description'*/}
        {/*    parentForm={form}*/}
        {/*    maxLength={1000}*/}
        {/*  />*/}
        {/*</div>*/}
      </Form>
    );
  }
);

EditOrganizerForm.displayName = 'EditOrganizerForm';
